'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { AlertCircle, Maximize2, Loader2 } from 'lucide-react';
import { createLogger } from '@/shared/lib/logger';
import { useResizeHandles } from '@/shared/lib/hooks/useResizeHandles';
import {
  ResizeHandles,
  CropOverlay,
  GridLines,
} from '@/shared/ui/image-editor';

const logger = createLogger('ImageEditor');

interface ImageEditorProps {
  imageUrl: string;
  aspectRatio?: number;
  isOpen?: boolean;
  onSave: (croppedBlob: Blob) => void | Promise<void>;
  onCancel: () => void;
  title?: string;
  saveButtonText?: string;
  minWidth?: number;
  minHeight?: number;
}

export function ImageEditor({
  imageUrl,
  aspectRatio,
  isOpen = true,
  onSave,
  onCancel,
  title,
  saveButtonText,
  minWidth = 100,
  minHeight = 100,
}: ImageEditorProps) {
  const t = useTranslations('imageCrop');
  const tCommon = useTranslations('common');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageNaturalSize, setImageNaturalSize] = useState({
    width: 0,
    height: 0,
  });
  const [displayScale, setDisplayScale] = useState(1);
  const [isSaving, setIsSaving] = useState(false);

  // Use shared resize handles hook
  const {
    area: cropArea,
    setArea: setCropArea,
    handleResizeStart,
    handleTouchResizeStart,
    handleDragStart,
    handleTouchDragStart,
    handleTouchMove,
    handleTouchEnd,
  } = useResizeHandles({
    initialArea: { x: 0, y: 0, width: 100, height: 100 },
    constraints: {
      minWidth,
      minHeight,
      aspectRatio,
      bounds: imageNaturalSize.width > 0 ? imageNaturalSize : undefined,
    },
    displayScale,
  });

  useEffect(() => {
    if (!imageUrl) {
      setImageError('No image URL provided');
      return;
    }

    const img = new Image();

    img.onload = () => {
      setImageNaturalSize({ width: img.width, height: img.height });

      // Calculate initial scale based on viewport
      const maxWidth = window.innerWidth * 0.8 - 40; // 80% of viewport minus padding
      const maxHeight = window.innerHeight * 0.6; // 60% of viewport height

      const scaleX = maxWidth / img.width;
      const scaleY = maxHeight / img.height;
      const scale = Math.min(scaleX, scaleY, 1); // Don't scale up

      setDisplayScale(scale);

      // Set initial crop area to center of image (50% size)
      const initialWidth = img.width * 0.5;
      const initialHeight = aspectRatio
        ? initialWidth / aspectRatio
        : img.height * 0.5;

      setCropArea({
        x: (img.width - initialWidth) / 2,
        y: (img.height - initialHeight) / 2,
        width: initialWidth,
        height: initialHeight,
      });

      setImageLoaded(true);
      setImageError(null);
    };

    img.onerror = (e) => {
      console.error('Failed to load image:', e);
      setImageError('Failed to load image. Please try again.');
      setImageLoaded(false);
    };

    img.src = imageUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, aspectRatio, setCropArea]);

  const handleSave = useCallback(async () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image) {
      console.error('Canvas or image not ready');
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Failed to get canvas context');
      return;
    }

    setIsSaving(true);

    try {
      // Set canvas size to crop area size
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;

      // Draw cropped image
      ctx.drawImage(
        image,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        cropArea.width,
        cropArea.height
      );

      // Convert to blob
      canvas.toBlob(
        async (blob) => {
          if (blob) {
            logger.info('Blob created, size:', blob.size);
            await onSave(blob);
          } else {
            console.error('Failed to create blob from canvas');
          }
          setIsSaving(false);
        },
        'image/jpeg',
        0.9
      );
    } catch (error) {
      console.error('Error saving image:', error);
      setIsSaving(false);
    }
  }, [cropArea, onSave]);

  const handleFitToWindow = useCallback(() => {
    if (!imageNaturalSize.width || !imageNaturalSize.height) return;

    setCropArea({
      x: 0,
      y: 0,
      width: imageNaturalSize.width,
      height: imageNaturalSize.height,
    });
  }, [imageNaturalSize, setCropArea]);

  if (imageError) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {tCommon('error')}
            </DialogTitle>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{imageError}</AlertDescription>
          </Alert>
          <div className="flex justify-end">
            <Button onClick={onCancel}>{tCommon('close')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!imageLoaded) {
    logger.debug(
      'Still loading, imageLoaded=',
      imageLoaded,
      'imageUrl=',
      imageUrl
    );
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="sr-only">
              {tCommon('loadingImage')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
            <span>{tCommon('loadingImage')}</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-6xl max-h-[95vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title || t('title')}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          <div
            ref={containerRef}
            className="relative inline-block bg-muted rounded-lg p-4"
            style={{ minWidth: '600px', minHeight: '400px' }}
          >
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Edit preview"
              className="block"
              style={{
                width: imageNaturalSize.width * displayScale,
                height: imageNaturalSize.height * displayScale,
                maxWidth: 'none',
              }}
              draggable={false}
              onLoad={() => logger.debug('Image element loaded in DOM')}
              onError={(e) => console.error('Image element error in DOM:', e)}
            />

            {/* Dark overlay outside crop area */}
            <CropOverlay
              x={cropArea.x}
              y={cropArea.y}
              width={cropArea.width}
              height={cropArea.height}
              displayScale={displayScale}
            />

            {/* Crop area with handles */}
            <div
              className="absolute border-2 border-white cursor-move touch-none"
              style={{
                left: cropArea.x * displayScale + 16,
                top: cropArea.y * displayScale + 16,
                width: cropArea.width * displayScale,
                height: cropArea.height * displayScale,
              }}
              onMouseDown={handleDragStart}
              onTouchStart={handleTouchDragStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <ResizeHandles
                onMouseDown={handleResizeStart}
                onTouchStart={handleTouchResizeStart}
              />
              <GridLines />
            </div>
          </div>
        </div>

        <canvas ref={canvasRef} className="hidden" />

        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-form-help text-muted-foreground">
            <span>
              {t('cropSize', {
                width: Math.round(cropArea.width),
                height: Math.round(cropArea.height),
              })}
            </span>
            {aspectRatio && (
              <span>{t('aspectValue', { value: aspectRatio.toFixed(2) })}</span>
            )}
            <span className="text-list-item-meta sm:hidden">{t('touchHint')}</span>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleFitToWindow}>
              <Maximize2 className="w-4 h-4 mr-2" />
              {t('fullImage')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={isSaving}
            >
              {tCommon('cancel')}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {tCommon('saving')}
                </>
              ) : (
                saveButtonText || t('saveButton')
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
